import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useBundleConversionStats } from '@/hooks/useBundleConversion';
import { TrendingUp, Trophy, Target, DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

export function BundleConversionAnalytics() {
  const { data: stats, isLoading } = useBundleConversionStats();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-[300px]" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-[200px]" />
          <Skeleton className="h-[200px]" />
        </div>
      </div>
    );
  }

  if (!stats || stats.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground text-center">No bundle conversion data available yet.</p>
        </CardContent>
      </Card>
    );
  }

  const chartData = stats.slice(0, 10).map(s => ({
    name: s.bundle_name.length > 15 ? s.bundle_name.slice(0, 15) + '...' : s.bundle_name,
    rate: Math.round(s.conversion_rate),
    won: s.won_deals,
    lost: s.lost_deals,
  }));

  const topPerformer = stats[0];
  const totalWonRevenue = stats.reduce((sum, s) => sum + s.total_won_revenue, 0);

  const getConversionColor = (rate: number) => {
    if (rate >= 70) return 'hsl(var(--chart-1))';
    if (rate >= 50) return 'hsl(var(--chart-2))';
    if (rate >= 30) return 'hsl(var(--chart-3))';
    return 'hsl(var(--chart-4))';
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Trophy className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Top Bundle</p>
                <p className="font-semibold truncate">{topPerformer?.bundle_name || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-chart-1/10">
                <TrendingUp className="h-5 w-5 text-chart-1" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Best Conversion</p>
                <p className="font-semibold">{topPerformer?.conversion_rate.toFixed(1) || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-chart-2/10">
                <Target className="h-5 w-5 text-chart-2" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Won Deals</p>
                <p className="font-semibold">{stats.reduce((sum, s) => sum + s.won_deals, 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-chart-3/10">
                <DollarSign className="h-5 w-5 text-chart-3" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Won Revenue</p>
                <p className="font-semibold">${totalWonRevenue.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversion Rate Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Bundle Conversion Rates</CardTitle>
          <CardDescription>Win rate for deals containing each bundle</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="name" width={120} />
                <Tooltip
                  formatter={(value: number) => [`${value}%`, 'Conversion Rate']}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="rate" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getConversionColor(entry.rate)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Bundle Performance Details</CardTitle>
          <CardDescription>Detailed conversion metrics for each bundle</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {stats.map((bundle) => (
              <div key={bundle.bundle_id} className="p-4 rounded-lg border bg-card">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium">{bundle.bundle_name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {bundle.total_deals} total deals · ${bundle.total_won_revenue.toLocaleString()} won revenue
                    </p>
                  </div>
                  <Badge variant={bundle.conversion_rate >= 50 ? 'default' : 'secondary'}>
                    {bundle.conversion_rate.toFixed(1)}% win rate
                  </Badge>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Conversion Progress</span>
                    <span>{bundle.won_deals} won / {bundle.won_deals + bundle.lost_deals} closed</span>
                  </div>
                  <Progress value={bundle.conversion_rate} className="h-2" />
                </div>

                <div className="flex gap-4 mt-3 text-sm">
                  <span className="text-chart-1">✓ {bundle.won_deals} Won</span>
                  <span className="text-destructive">✗ {bundle.lost_deals} Lost</span>
                  <span className="text-muted-foreground">○ {bundle.open_deals} Open</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
